import { IntlContext } from '@jutro/locale';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import { Currency as CurrencyField } from 'gw-components-platform-react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { CheckboxField } from '@jutro/legacy/components';
import messages from './PaymentHistory.messages';
import metadata from './PaymentHistory.metadata.json5';
import styles from './PaymentHistory.module.scss';

const getBillStatus = (item, index, property) => {
    if (item.paidStatus === 'fullypaid') {
        return messages.paid;
    }
    
    return {
        id: `typekey.InvoiceStatus.${item[property.id]}`,
        defaultMessage: item[property.id],
    };
};

const renderFormattedCurrencyField = (item, index) => (
        <div className={styles.currencyContainer}>
            <CurrencyField
                id={`currency_${index}`}
                value={item}
                readOnly
                hideLabel
            />
        </div>
    );

const getFormattedCurrency = (item, index, property) => renderFormattedCurrencyField(item[property.id]);

const getCell = (item, index, property) => item[property.id];

function PaymentHistory(props) {

    const intl = useContext(IntlContext);
    const [paymentHistory, setPaymentHistory] = useState([])
    const [totalAmount, setTotalAmount] = useState([]);

    const { AccountBillingDetailsService: BillingService } = useDependencies(
        'AccountBillingDetailsService'
    );

    const {
        termNumber,
        selectedPolicy,
        selectedInvoices,
        onUpdateSelectedInvoices,
        authHeader
    } = props;

    // 2025-02-17 - Debra Frum - DE18483 - Format dates the same as the View Invoices section on the page
    const getFormattedDate = useCallback((date) => 
        intl.formatDate(new Date(date), {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
         }),
    [intl]);
    
    const getPaymentHistoryDataTable = useCallback((paymentsData) => {
        let paymentHistoryArrayResult = [];

        paymentHistoryArrayResult = paymentsData?.map(data => {
            const formattedAppliedDate = _.isUndefined(data.appliedDate) ? undefined : getFormattedDate(data.appliedDate);
            const formattedReceivedDate = _.isUndefined(data.receivedDate) ? undefined : getFormattedDate(data.receivedDate);
            const formattedReversedDate = _.isUndefined(data.reversedDate) ? undefined : getFormattedDate(data.reversedDate);

            const objData = {
                appliedDate: formattedAppliedDate,
                receivedDate: formattedReceivedDate,
                reversedDate: formattedReversedDate,
                refNumber: data.checkReference,
                amount: data.amount,
                paymentMethod: data.paymentMethod
            }

            return objData;
        })

        return paymentHistoryArrayResult;
    }, [getFormattedDate]);

    const fetchAccountLevelInvoiceStreams = useCallback(async () => {
        let paymentSummary;

        if (termNumber.toString().toLowerCase() === 'all') {
            paymentSummary = await BillingService.getPolicyPaymentHistory_TWIA(selectedPolicy, authHeader);
        } else {
            paymentSummary = await BillingService.getPolicyTermPaymentHistory_TWIA(selectedPolicy, termNumber.toString(), authHeader);
        }

        const paymentwithoutFilter = _.filter(paymentSummary, (payment) => _.isUndefined((payment.reversedDate)));
        const aTotalAmount = {'amount' : _.sum(paymentwithoutFilter.map(payment => _.get(payment,'amount.amount'))),'currency':'usd'};
        
        setTotalAmount(aTotalAmount);

        return paymentSummary;
    }, [BillingService, authHeader, selectedPolicy, termNumber]);

    useEffect(() => {
        if (termNumber !== "" && termNumber !== undefined) {
            fetchAccountLevelInvoiceStreams().then(retValues => {
                if (retValues) {
                    const tableData = getPaymentHistoryDataTable(retValues);
                    
                    setPaymentHistory(tableData)
                }
            });
        }

    }, [fetchAccountLevelInvoiceStreams, getPaymentHistoryDataTable, termNumber]);

    const getCheckboxDataCell = useCallback(
        (item, index, property) => {
            const value = item[property.id];
            const selectedIds = [...selectedInvoices];
            const isChecked = selectedIds.indexOf(value) > -1;

            return (
                <CheckboxField
                    id={`${property.id}_${index}`}
                    name={`${property.id}_${index}`}
                    value={isChecked}
                    onValueChange={selected => {
                        if (selected) {
                            selectedIds.push(value);
                            onUpdateSelectedInvoices(selectedIds);
                        } else {
                            const updated = selectedIds.filter(
                                iter => iter !== value
                            );

                            onUpdateSelectedInvoices(updated);
                        }
                    }}
                />
            );
        },
        [selectedInvoices, onUpdateSelectedInvoices]
    );

    const overrideProps = {
        paymentHistoryTable: {
            data: paymentHistory
        },
        totalPaymentHistoryAmount: {
            value: totalAmount,
            visible: totalAmount?.amount > 0
        },
        totalInvoiceAmountLabel: {
            visible: totalAmount?.amount > 0           
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            getFormattedDate,
            getBillStatus,
            getFormattedCurrency,
            getCell,
            getCheckboxDataCell,
        },
    };

    return (
        <MetadataContent
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            {...resolvers}
        />
    );
}

PaymentHistory.propTypes = {
    invoiceSummary: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    selectedInvoices: PropTypes.arrayOf(PropTypes.string),
    onUpdateSelectedInvoices: PropTypes.func,
    showSelect: PropTypes.bool,
    showPaidDate: PropTypes.bool,
};

PaymentHistory.defaultProps = {
    selectedInvoices: [],
    onUpdateSelectedInvoices: undefined,
    showSelect: false,
    showPaidDate: false,
};

export default PaymentHistory;
